/**
 * Email Management Page
 * AIDLC Administrative Backend
 *
 * Single page at /email: the email signature template for each signature profile.
 * The portal sends only as agents@ldaja.com and reads no mailbox or OneDrive (KD-1 E4, KD-1b).
 *
 * Personas: ops
 */

import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import * as emailService from '../../services/emailService';
import type {
  EmailSignatureTemplate,
  EmailIntegrationConfig,
} from '../../types/email';

// ── Main Page ─────────────────────────────────────────────────────

export function EmailManagementPage() {
  const [integrations, setIntegrations] = useState<EmailIntegrationConfig[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState<string>('');
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  // Load signature profiles on mount
  useEffect(() => {
    emailService.getIntegrations()
      .then((data) => {
        setIntegrations(data);
        if (data.length > 0 && !selectedMailbox) {
          setSelectedMailbox(data[0].userEmail);
        }
      })
      .catch(() => {
        // Integrations are store-only, should always work
        setIntegrations([]);
      })
      .finally(() => setLoadingIntegrations(false));
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Portal email sends as agents@ldaja.com. Set the signature template here.
          </p>
        </div>

        {/* Profile Selector */}
        {integrations.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Profile:</label>
            <select
              value={selectedMailbox}
              onChange={(e) => setSelectedMailbox(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            >
              {integrations.map((i) => (
                <option key={i.id} value={i.userEmail}>
                  {i.displayName} ({i.userEmail})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loadingIntegrations ? (
        <LoadingSpinner message="Loading email configuration..." />
      ) : (
        <SignatureTab userEmail={selectedMailbox} integrations={integrations} />
      )}

      {/* Integration Management */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <IntegrationManager
          integrations={integrations}
          onUpdate={setIntegrations}
          onSelect={setSelectedMailbox}
        />
      </div>
    </div>
  );
}

// ── Signature Tab ─────────────────────────────────────────────────

function SignatureTab({ userEmail, integrations }: { userEmail: string; integrations: EmailIntegrationConfig[] }) {
  const [template, setTemplate] = useState<EmailSignatureTemplate | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadSignature = useCallback(async () => {
    if (!userEmail) { setLoading(false); return; }
    setLoading(true);
    setSaved(false);
    setSaveError(null);
    try {
      const [tmpl, html] = await Promise.all([
        emailService.getSignatureTemplate(userEmail),
        emailService.getSignaturePreview(userEmail),
      ]);
      setTemplate(tmpl);
      setPreview(html);
    } catch {
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    loadSignature();
  }, [loadSignature]);

  const handleSave = async () => {
    if (!template || !userEmail) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const updated = await emailService.updateSignatureTemplate(userEmail, template);
      setTemplate(updated);
      const html = await emailService.getSignaturePreview(userEmail);
      setPreview(html);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError('Failed to save signature template.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof EmailSignatureTemplate, value: string) => {
    setTemplate((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  if (!userEmail) {
    return (
      <div className="text-center py-12 text-gray-500">
        No signature profile selected. Add one below to get started.
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading signature template..." />;
  if (!template) return <div className="text-center py-8 text-gray-500">Failed to load signature template</div>;

  const currentIntegration = integrations.find(i => i.userEmail === userEmail);

  return (
    <div className="grid grid-cols-2 gap-8">
      {/* Editor */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Signature Template</h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {currentIntegration?.displayName || userEmail}
          </span>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valediction (sign-off)</label>
            <input
              type="text"
              value={template.valediction}
              onChange={(e) => updateField('valediction', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
              placeholder="Onward and upward,"
            />
            <p className="text-xs text-gray-400 mt-1">Agent-generated sign-off (can be overridden per reply)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Signer Name</label>
            <input
              type="text"
              value={template.signerName}
              onChange={(e) => updateField('signerName', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={template.signerTitle}
              onChange={(e) => updateField('signerTitle', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={template.signerEmail}
              onChange={(e) => updateField('signerEmail', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input
              type="text"
              value={template.tagline}
              onChange={(e) => updateField('tagline', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Saved
              </span>
            )}
            {saveError && (
              <span className="text-sm text-red-600">{saveError}</span>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
        <div className="border border-gray-200 rounded-lg p-6 bg-white">
          <div className="mb-4 pb-4 border-b border-gray-100">
            <p className="text-sm text-gray-500 mb-2">This is what your signature looks like in an email:</p>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            <p>Hi [Recipient],</p>
            <br />
            <p>Thank you for reaching out. I wanted to follow up on our conversation...</p>
            <br />
          </div>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview) }} />
        </div>
      </div>
    </div>
  );
}

// ── Integration Manager ───────────────────────────────────────────

function FeatureToggle({
  label,
  enabled,
  onChange,
}: {
  label: string;
  enabled: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
          enabled ? 'bg-ldaja-blue' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </button>
      <span className="text-[11px] text-gray-600">{label}</span>
    </label>
  );
}

function IntegrationManager({
  integrations,
  onUpdate,
  onSelect,
}: {
  integrations: EmailIntegrationConfig[];
  onUpdate: (configs: EmailIntegrationConfig[]) => void;
  onSelect: (email: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const extractError = (err: unknown): string => {
    const resp = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    return resp ?? 'An unexpected error occurred';
  };

  const handleAdd = async () => {
    if (!newEmail || !newName) return;
    setError(null);
    try {
      const created = await emailService.addIntegration({ userEmail: newEmail, displayName: newName });
      onUpdate([...integrations, created]);
      onSelect(created.userEmail);
      setAdding(false);
      setNewEmail('');
      setNewName('');
    } catch (err) {
      setError(extractError(err));
    }
  };

  const handleRemove = async (id: string) => {
    setError(null);
    try {
      await emailService.removeIntegration(id);
      const updated = integrations.filter((i) => i.id !== id);
      onUpdate(updated);
      if (updated.length > 0) onSelect(updated[0].userEmail);
    } catch (err) {
      setError(extractError(err));
    }
    setConfirmDeleteId(null);
  };

  const handleToggleProtected = async (integration: EmailIntegrationConfig) => {
    setError(null);
    try {
      const updated = await emailService.updateIntegration(integration.id, {
        protected: !integration.protected,
      });
      onUpdate(integrations.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(extractError(err));
    }
  };

  const handleToggleFeature = async (
    integration: EmailIntegrationConfig,
    feature: 'signature',
    value: boolean
  ) => {
    setError(null);
    try {
      const updated = await emailService.updateIntegration(integration.id, {
        features: { ...integration.features, [feature]: value },
      });
      onUpdate(integrations.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(extractError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Signature Profiles</h3>
        <button
          onClick={() => setAdding((v) => !v)}
          className="text-xs text-ldaja-blue hover:underline"
        >
          {adding ? 'Cancel' : '+ Add Profile'}
        </button>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
          <p className="text-xs text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {adding && (
        <div className="flex items-end gap-3 mb-4 bg-gray-50 p-4 rounded-lg">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@ldaja.com"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Dan Dagnall"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={!newEmail || !newName}
            className="px-4 py-1.5 text-sm bg-ldaja-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
          >
            Add
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="bg-white border border-gray-200 rounded-lg px-4 py-3"
          >
            {/* Header row: name + actions */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{integration.displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{integration.userEmail}</p>
                </div>
                {integration.protected && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded border border-amber-200 shrink-0">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    Protected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Protection toggle */}
                <button
                  onClick={() => handleToggleProtected(integration)}
                  className={`p-1 rounded transition-colors ${
                    integration.protected
                      ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                  title={integration.protected ? 'Remove protection' : 'Protect profile'}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    {integration.protected ? (
                      <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    ) : (
                      <path d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    )}
                  </svg>
                </button>
                {/* Delete button — hidden for protected integrations */}
                {!integration.protected && (
                  <button
                    onClick={() => setConfirmDeleteId(integration.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                    title="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Feature toggles */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <FeatureToggle
                label="Signature"
                enabled={integration.features?.signature ?? true}
                onChange={(v) => handleToggleFeature(integration, 'signature', v)}
              />
            </div>

            {/* Delete confirmation overlay */}
            {confirmDeleteId === integration.id && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700 mb-2">
                  Delete <strong>{integration.displayName}</strong>? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRemove(integration.id)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-3 py-1 text-xs bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
